/**
 * src/ui/app.ts — A CASCA DO APP HÍBRIDO: decide o modo e monta as rotas.
 *
 * Este arquivo é a divisão de `bootReceptor` pedida no HYBRID §9: o roteador, o
 * boot e a decisão "sou tela ou sou gestor?" moram aqui; a máquina de estado do
 * player (Executor, watchdog, heartbeat, polling) continua em `main.ts`.
 *
 * ⚠️ INVARIANTE DE DOM QUE ESTE ARQUIVO NÃO PODE QUEBRAR (HYBRID §10, e é
 * ACEITE, não sugestão):
 *   - `#surface-image`/`#surface-video` são criados UMA VEZ pelo `index.html` e
 *     vivem em `#app`. O roteador NUNCA recria `#app`, e só escreve dentro de
 *     `#overlay` (nós de estado). Recriar os elementos de mídia produz flash
 *     preto e reinicia o item atual (PLAYER §4.2).
 *   - O modo gestor monta em `#gestor`, um contêiner SEPARADO. A TV nunca o vê.
 *   - `body[data-mode]` escopa o `styles.css`: o CSS do gerenciador não pode
 *     vazar para a TV, nem o da TV para o painel.
 *   - O `Executor` nasce UMA vez por sessão de player e NÃO é parado por
 *     `popstate`: voltar/avançar no histórico não derruba uma tela no ar.
 */
import './styles.css';
import { registerHttpGateway, isDemoRequested, isDemoBuilt } from '../api';
import {
  CredentialStore,
  HttpGateway,
  LinkRequestClient,
  ScreensClient,
  Session,
  browserLocalStorage,
  memoryStore,
  resolveApiBaseUrl,
} from '../../net';
import { Router } from '../router';
import { bootPlayer } from './main';
import { renderLogin } from './login';
import { renderEscolher } from './escolher';
import { mountVinculo, renderVinculo, renderVinculoExpirado, renderVinculoRecusado } from './vinculo';
import { renderFaixa } from './espera';

const APP_VERSION = '0.2.0';

/** Caminho da rota oculta do dono. Não linkar em lugar nenhum. */
export const DONO_PATH = '/_dono';

registerHttpGateway(opts => new HttpGateway({ ...opts, baseUrl: resolveApiBaseUrl(), appVersion: APP_VERSION }));

/** Rótulo curto do dispositivo — aparece no celular que aprova (TV-QR §3.1). */
function deviceLabel(): string {
  const browser = /Edg\//.test(navigator.userAgent)
    ? 'Edge'
    : /Chrome\//.test(navigator.userAgent)
      ? 'Chrome'
      : /Safari\//.test(navigator.userAgent)
        ? 'Safari'
        : /Firefox\//.test(navigator.userAgent)
          ? 'Firefox'
          : 'Navegador';
  return `Navegador — ${browser}`;
}

export function bootApp(doc: Document = document): void {
  const app = doc.getElementById('app');
  if (!app) {
    return;
  }
  const body = doc.body;
  const isDemo = isDemoRequested(globalThis.location?.search ?? '', globalThis.location?.hash ?? '') || isDemoBuilt();
  if (isDemo) {
    // A demo é o player puro: sem login, sem rota, exatamente como era antes.
    body.dataset['mode'] = 'player';
    bootPlayer(doc);
    return;
  }

  const overlay = doc.createElement('div');
  overlay.id = 'overlay';
  app.append(overlay);
  const gestor = doc.createElement('div');
  gestor.id = 'gestor';
  gestor.hidden = true;
  doc.body.append(gestor);

  const storage = browserLocalStorage();
  const store = storage ?? memoryStore();
  const storeIsDurable = storage !== null;
  const credentials = new CredentialStore(store);
  const session = new Session({ store, onSessionLost: () => router.navigate('/entrar', true) });
  const screens = new ScreensClient(session);

  let playerStarted = false;
  const router = new Router({ fallback: '/' });
  let vinculoAbort: AbortController | null = null;
  let vinculoClient: LinkRequestClient | null = null;

  const modoPlayer = (): void => {
    body.dataset['mode'] = 'player';
    gestor.hidden = true;
    overlay.hidden = false;
  };

  const modoGestor = (): void => {
    body.dataset['mode'] = 'gestor';
    overlay.hidden = true;
    gestor.hidden = false;
  };

  const mostrar = (no: HTMLElement, em: 'overlay' | 'gestor'): void => {
    if (em === 'overlay') {
      overlay.replaceChildren(no);
    } else {
      gestor.replaceChildren(no);
    }
  };

  /* --------------------------------------------------------- modo player */
  const entrarNoPlayer = (): void => {
    modoPlayer();
    if (playerStarted) {
      return;
    }
    playerStarted = true;
    const current = new CredentialStore(store).load();
    if (!current) {
      router.navigate('/vincular', true);
      return;
    }
    bootPlayer(doc, current);
  };

  /* ------------------------------------------------------- tela de vínculo */
  const telaVinculo = (): void => {
    modoGestor();
    vinculoAbort?.abort();
    const abort = new AbortController();
    vinculoAbort = abort;
    const client = new LinkRequestClient({
      store,
      baseUrl: resolveApiBaseUrl(),
      deviceLabel: deviceLabel(),
      appVersion: APP_VERSION,
      platform: 'web',
      onLog: (message, detail) => console.debug('[vinculo]', message, detail ?? ''),
    });
    vinculoClient = client;
    if (!storeIsDurable) {
      mostrar(
        appendFaixa(
          renderVinculoExpirado(),
          'Este navegador não guarda credenciais (modo anônimo). A tela não vai se manter vinculada.',
        ),
        'gestor',
      );
      return;
    }
    const desenhar = (): void => {
      const display = client.currentDisplay();
      const node = renderVinculo({ ...(display ? { display } : {}) });
      if (!storeIsDurable) {
        node.append(renderFaixa('Este navegador não guarda a identidade. Evite modo anônimo.'));
      }
      mostrar(node, 'gestor');
      const view = mountVinculo(node);
      if (display) {
        void view.setPayload(display.qrPayload).catch(() => console.warn('[vinculo] QR indisponível'));
      }
    };
    void client
      .run(state => {
        if (state.status === 'approved') {
          // Credencial já gravada em disco pelo client: só entra no player.
          router.navigate('/tela', true);
          return;
        }
        if (state.status === 'denied') {
          mostrar(renderVinculoRecusado(), 'gestor');
          return;
        }
        if (state.status === 'expired') {
          mostrar(renderVinculoExpirado(), 'gestor');
          return;
        }
        if (state.status === 'renewing') {
          mostrar(appendFaixa(renderVinculoExpirado(), 'Gerando um novo código…'), 'gestor');
          return;
        }
        if (state.status === 'offline') {
          const node = renderVinculo({ offline: true });
          mostrar(node, 'gestor');
          void mountVinculo(node).setPayload(client.currentDisplay()?.qrPayload ?? '').catch(() => {});
          return;
        }
        desenhar();
      }, abort.signal)
      .catch(() => mostrar(renderVinculoExpirado(), 'gestor'));
  };

  /* --------------------------------------------------------------- rotas */
  // Code-splitting deliberado (plano §3.3): `import()` por rota, para que o
  // grafo do modo player NÃO contenha o gerenciador. Uma TV em `/tela` não baixa
  // o painel — é por isso que este import não pode ser estático.
  const painel = async (): Promise<void> => {
    modoGestor();
    const { renderPainel } = await import('./painel');
    mostrar(renderPainel(session, screens, { onLinkByQr: () => router.navigate('/vincular') }), 'gestor');
  };

  router
    .on('/', async () => {
      // A decisão de modo mora AQUI e só aqui (HYBRID §4), para que um deep link
      // (`/vincular` do QR, `/tela` de um pushState) NÃO seja sobrescrito pelo
      // boot. A credencial de tela vence a sessão do gestor: uma TV que alguém
      // logou por engano não pode cair no gerenciador ao recarregar.
      if (credentials.load()) {
        router.navigate('/tela', true);
        return;
      }
      if (session.hasStoredSession) {
        modoGestor();
        // Esqueleto enquanto o refresh corre: rede lenta não deixa tela branca.
        mostrar(renderLoading(), 'gestor');
        const ok = await session.ensureSession();
        router.navigate(ok ? '/escolher' : '/entrar', true);
        return;
      }
      router.navigate('/entrar', true);
    })
    .on('/entrar', () => {
      modoGestor();
      mostrar(
        renderLogin(session, {
          onDone: () => router.navigate('/escolher'),
          onLinkByQr: () => router.navigate('/vincular'),
        }),
        'gestor',
      );
    })
    .on('/escolher', () => {
      modoGestor();
      mostrar(
        renderEscolher(screens, {
          credentialStore: credentials,
          alreadyScreen: credentials.load() !== null,
          onLinkByQr: () => router.navigate('/vincular'),
          onDone: () => router.navigate('/tela'),
        }),
        'gestor',
      );
    })
    .on('/tela', () => {
      if (!credentials.load()) {
        router.navigate('/vincular', true);
        return;
      }
      entrarNoPlayer();
    })
    .on('/vincular', telaVinculo)
    .on('/painel', painel)
    // Rota OCULTA do dono (operador): nenhum link do app aponta para cá. A
    // proteção é o token do operador validado no backend, não o caminho.
    .on(DONO_PATH, async () => {
      modoGestor();
      const { renderDono } = await import('./dono');
      mostrar(renderDono(), 'gestor');
    });

  /* ---------------------------------------------------------------- boot */
  // `start()` resolve a rota corrente: `/` decide o modo, um deep link abre a
  // rota pedida. A decisão NÃO é duplicada aqui — duas cópias divergiriam.
  globalThis.addEventListener?.(
    'pagehide',
    () => {
      vinculoAbort?.abort();
      vinculoClient?.stop();
      session.dispose();
    },
    { once: true },
  );

  router.start();
}

// Auto-boot: o `index.html` aponta para este módulo (HYBRID §3, rota `/`).
if (typeof document !== 'undefined' && document.getElementById('app')) {
  bootApp();
}

/** Esqueleto do boot: o refresh de rede não pode deixar a tela branca. */
function renderLoading(): HTMLElement {
  const node = document.createElement('section');
  node.className = 'pagina';
  node.dataset['papel'] = 'carregando';
  const titulo = document.createElement('h1');
  titulo.className = 'pagina__titulo';
  titulo.textContent = 'Abrindo…';
  node.append(titulo);
  return node;
}

function appendFaixa(node: HTMLElement, texto: string): HTMLElement {
  node.append(renderFaixa(texto, 'neutro'));
  return node;
}
